/**
 * Babeina PDP reviews — Judge.me as backend, Babeina UI for display + write drawer.
 * List: prefer Judge.me widget API (includes review photos/videos); fall back to
 * synced metafield HTML in <template>.
 * Write: Babeina drawer (same open/close as Shop the Look) → public POST /api/v1/reviews.
 */
(function () {
  'use strict';

  var STAR_PATH =
    'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
  var JM_CREATE_URL = 'https://judge.me/api/v1/reviews';
  var JM_WIDGET_URL = 'https://judge.me/reviews/reviews_for_widget';
  var REVIEWS_PER_PAGE = 5;
  var MEDIA_THUMB_MAX = 3;

  function qs(root, sel) {
    return (root || document).querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  var starGradSeq = 0;

  function starSvg(fill, size) {
    return (
      '<svg viewBox="0 0 24 24" width="' +
      size +
      '" height="' +
      size +
      '" aria-hidden="true"><path d="' +
      STAR_PATH +
      '" fill="' +
      fill +
      '"/></svg>'
    );
  }

  function starSvgPartial(fillRatio, size) {
    var r = Math.max(0, Math.min(1, fillRatio));
    if (r <= 0.001) return starSvg('#d0d0d0', size);
    if (r >= 0.999) return starSvg('#1d1d1f', size);
    starGradSeq += 1;
    var pct = (r * 100).toFixed(1);
    var gid = 'babeina-star-grad-' + starGradSeq;
    return (
      '<svg viewBox="0 0 24 24" width="' +
      size +
      '" height="' +
      size +
      '" aria-hidden="true">' +
      '<defs><linearGradient id="' +
      gid +
      '" x1="0" y1="0" x2="1" y2="0">' +
      '<stop offset="' +
      pct +
      '%" stop-color="#1d1d1f"/>' +
      '<stop offset="' +
      pct +
      '%" stop-color="#d0d0d0"/>' +
      '</linearGradient></defs>' +
      '<path d="' +
      STAR_PATH +
      '" fill="url(#' +
      gid +
      ')"/>' +
      '</svg>'
    );
  }

  function starsRow(score, size) {
    var s = parseFloat(score) || 0;
    var html = '';
    var i;
    for (i = 1; i <= 5; i++) {
      html += starSvgPartial(s - (i - 1), size);
    }
    return html;
  }

  function text(el) {
    return el ? (el.textContent || '').replace(/\s+/g, ' ').trim() : '';
  }

  function pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function parseTimestamp(tsEl) {
    if (!tsEl) return { display: '', iso: '' };
    var raw =
      tsEl.getAttribute('datetime') ||
      tsEl.getAttribute('data-content') ||
      text(tsEl);
    if (!raw) return { display: '', iso: '' };
    var d = new Date(raw);
    if (isNaN(d.getTime())) return { display: raw, iso: '' };
    // UTC calendar date so storefront TZ doesn't shift the review day
    var yy = String(d.getUTCFullYear()).slice(-2);
    return {
      display: pad2(d.getUTCMonth() + 1) + '/' + pad2(d.getUTCDate()) + '/' + yy,
      iso: d.toISOString(),
    };
  }

  function isVerifiedBuyer(rev) {
    var attr = (rev.getAttribute('data-verified-buyer') || '').toLowerCase();
    if (attr === 'true' || attr === '1') return true;
    if (attr === 'false' || attr === '0') return false;
    return !!qs(rev, '.jdgm-rev__buyer-badge, .jdgm-rev__buyer-badge-wrapper .jdgm-rev__buyer-badge');
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function decodeEntities(str) {
    if (!str) return '';
    var ta = document.createElement('textarea');
    ta.innerHTML = String(str);
    return ta.value;
  }

  function sizedMediaUrl(url, width) {
    if (!url) return '';
    var target = String(width || 320);
    try {
      var u = new URL(url, window.location.origin);
      u.searchParams.set('width', target);
      return u.toString();
    } catch (err) {
      if (/([?&])width=\d+/.test(url)) {
        return url.replace(/([?&])width=\d+/, '$1width=' + target);
      }
      return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + target;
    }
  }

  function formatApiTimestamp(raw) {
    if (!raw) return { display: '', iso: '' };
    var d = new Date(raw);
    if (isNaN(d.getTime())) return { display: String(raw), iso: '' };
    var yy = String(d.getUTCFullYear()).slice(-2);
    return {
      display: pad2(d.getUTCMonth() + 1) + '/' + pad2(d.getUTCDate()) + '/' + yy,
      iso: d.toISOString(),
    };
  }

  function mediaFromApiReview(review) {
    var media = [];
    var hosted = Array.isArray(review.media_platform_hosted_video_infos)
      ? review.media_platform_hosted_video_infos
      : [];
    var i;
    for (i = 0; i < hosted.length; i++) {
      var v = hosted[i] || {};
      var videoSrc =
        v.url || v.video_url || v.original_url || v.mp4_url || '';
      if (!videoSrc) continue;
      media.push({
        type: 'video',
        src: videoSrc,
        poster: sizedMediaUrl(
          v.thumbnail_url || v.poster_url || '',
          320
        ),
        href: videoSrc,
      });
    }

    var pics = Array.isArray(review.pictures_urls) ? review.pictures_urls : [];
    for (i = 0; i < pics.length; i++) {
      var pic = pics[i];
      var original =
        typeof pic === 'string'
          ? pic
          : pic.original || pic.huge || pic.compact || pic.small || '';
      var thumb =
        typeof pic === 'string'
          ? pic
          : pic.compact || pic.small || pic.huge || pic.original || '';
      if (!original && !thumb) continue;
      media.push({
        type: 'image',
        src: sizedMediaUrl(thumb || original, 320),
        href: original || thumb,
      });
    }
    return media;
  }

  function mediaFromDomReview(rev) {
    var media = [];
    var seen = {};

    qsa(rev, 'video source, video').forEach(function (el) {
      var src =
        el.getAttribute('src') ||
        (el.tagName === 'VIDEO' ? el.currentSrc || el.src : '') ||
        '';
      if (!src || seen[src]) return;
      seen[src] = true;
      var videoEl = el.tagName === 'VIDEO' ? el : el.parentElement;
      media.push({
        type: 'video',
        src: src,
        poster: videoEl ? videoEl.getAttribute('poster') || '' : '',
        href: src,
      });
    });

    qsa(rev, '.jdgm-rev__pic-img, .jdgm-rev__pics img, .jdgm-rev__media img').forEach(
      function (img) {
        var href =
          img.getAttribute('data-original-url') ||
          img.getAttribute('data-huge-url') ||
          img.getAttribute('data-src') ||
          img.getAttribute('src') ||
          '';
        var src =
          img.getAttribute('data-compact-url') ||
          img.getAttribute('data-src') ||
          img.getAttribute('src') ||
          href;
        if (!src || seen[src]) return;
        seen[src] = true;
        media.push({
          type: 'image',
          src: sizedMediaUrl(src, 320),
          href: href || src,
        });
      }
    );

    return media;
  }

  function mapApiReviewItem(rev) {
    var ts = formatApiTimestamp(rev.created_at);
    return {
      author: rev.reviewer_name || 'Customer',
      score: rev.rating != null ? String(rev.rating) : '',
      bodyHtml: rev.body_html || '',
      title: decodeEntities(rev.title || ''),
      timestamp: ts.display,
      timestampIso: ts.iso,
      verified: !!rev.verified_buyer,
      media: mediaFromApiReview(rev),
    };
  }

  function mapApiReviews(payload) {
    var reviews = (payload.reviews || []).map(mapApiReviewItem);
    var count = parseInt(payload.number_of_reviews, 10) || reviews.length;
    var totalPages =
      parseInt(payload.total_pages, 10) ||
      Math.max(1, Math.ceil(count / REVIEWS_PER_PAGE));
    return {
      reviews: reviews,
      average:
        payload.average_rating != null ? String(payload.average_rating) : '',
      count: count,
      totalPages: totalPages,
    };
  }

  function parseReviewsFromTemplate(tpl) {
    if (!tpl) return { reviews: [], average: '', count: 0 };
    var frag = tpl.content.cloneNode(true);
    var widg = qs(frag, '.jdgm-rev-widg');
    var average = widg ? widg.getAttribute('data-average-rating') || '' : '';
    var count = widg
      ? parseInt(widg.getAttribute('data-number-of-reviews') || '0', 10) || 0
      : 0;

    var reviews = qsa(frag, '.jdgm-rev').map(function (rev) {
      var ratingEl = qs(rev, '.jdgm-rev__rating');
      var bodyEl = qs(rev, '.jdgm-rev__body');
      var authorEl = qs(rev, '.jdgm-rev__author');
      var tsEl = qs(rev, '.jdgm-rev__timestamp');
      var titleEl = qs(rev, '.jdgm-rev__title');
      var score = ratingEl ? ratingEl.getAttribute('data-score') || '' : '';
      var ts = parseTimestamp(tsEl);

      return {
        author: text(authorEl) || 'Customer',
        score: score,
        bodyHtml: bodyEl ? bodyEl.innerHTML : '',
        title: text(titleEl),
        timestamp: ts.display,
        timestampIso: ts.iso,
        verified: isVerifiedBuyer(rev),
        media: mediaFromDomReview(rev),
      };
    });

    return { reviews: reviews, average: average, count: count };
  }

  function renderMedia(rev) {
    var items = Array.isArray(rev.media) ? rev.media : [];
    if (!items.length) return '';

    var author = rev.author || 'Customer';
    var shown = items.slice(0, MEDIA_THUMB_MAX);
    var extra = items.length - shown.length;
    var html = shown
      .map(function (item, index) {
        var label =
          escapeHtml(author) +
          ' review ' +
          (item.type === 'video' ? 'video' : 'photo') +
          (items.length > 1 ? ' ' + (index + 1) : '');
        if (item.type === 'video') {
          var poster = item.poster
            ? '<img class="babeina-rev__media-img" src="' +
              escapeHtml(item.poster) +
              '" alt="" loading="lazy" width="72" height="72" />'
            : '<span class="babeina-rev__media-video-fallback" aria-hidden="true"></span>';
          return (
            '<a class="babeina-rev__media-item babeina-rev__media-item--video" href="' +
            escapeHtml(item.href || item.src) +
            '" target="_blank" rel="noopener noreferrer" aria-label="' +
            label +
            '">' +
            poster +
            '<span class="babeina-rev__media-play" aria-hidden="true"></span>' +
            '</a>'
          );
        }
        return (
          '<a class="babeina-rev__media-item" href="' +
          escapeHtml(item.href || item.src) +
          '" target="_blank" rel="noopener noreferrer">' +
          '<img class="babeina-rev__media-img" src="' +
          escapeHtml(item.src) +
          '" alt="' +
          label +
          '" loading="lazy" width="72" height="72" />' +
          '</a>'
        );
      })
      .join('');

    if (extra > 0) {
      html +=
        '<span class="babeina-rev__media-more" aria-label="' +
        extra +
        ' more media">' +
        '+' +
        extra +
        '</span>';
    }

    return (
      '<div class="babeina-rev__media" role="group" aria-label="Review media">' +
      html +
      '</div>'
    );
  }

  function reviewBodyText(html) {
    var node = document.createElement('div');
    node.innerHTML = html || '';
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function reviewTime(iso) {
    var time = Date.parse(iso || '');
    return isNaN(time) ? null : time;
  }

  function prepareReviews(reviews) {
    var out = [];
    (reviews || []).forEach(function (rev) {
      var body = reviewBodyText(rev.bodyHtml);
      var author = rev.author || '';
      var time = reviewTime(rev.timestampIso);
      var duplicate = out.some(function (kept) {
        if ((kept.author || '') !== author) return false;
        if (reviewBodyText(kept.bodyHtml) !== body) return false;
        var keptTime = reviewTime(kept.timestampIso);
        if (time == null || keptTime == null) return true;
        return Math.abs(time - keptTime) <= 60000;
      });
      if (duplicate) return;
      var title = String(rev.title || '').replace(/(?:\.\.\.|…)\s*$/, '').trim();
      if (title && body.indexOf(title) === 0) title = '';
      rev.title = title;
      out.push(rev);
    });
    return out;
  }

  function renderList(listEl, data) {
    if (!listEl) return;
    data.reviews = prepareReviews(data.reviews);
    if (!data.reviews.length) {
      listEl.innerHTML =
        '<li class="babeina-rev__empty">Be the first to write a review</li>';
      return;
    }

    listEl.innerHTML = data.reviews
      .map(function (rev) {
        var scoreNum = rev.score
          ? '<span class="babeina-rev__score">' +
            parseFloat(rev.score).toFixed(1) +
            '</span>'
          : '';
        var starsHtml = rev.score
          ? '<span class="babeina-rev__stars" aria-hidden="true">' +
            starsRow(rev.score, 12) +
            '</span>'
          : '';
        var title = rev.title
          ? '<span class="babeina-rev__title">' + escapeHtml(rev.title) + '</span>'
          : '';
        var verified = rev.verified
          ? '<span class="babeina-rev__verified">Verified buyer</span>'
          : '';
        var date = rev.timestamp
          ? '<time class="babeina-rev__date"' +
            (rev.timestampIso
              ? ' datetime="' + escapeHtml(rev.timestampIso) + '"'
              : '') +
            '>' +
            escapeHtml(rev.timestamp) +
            '</time>'
          : '';
        var hasMedia = Array.isArray(rev.media) && rev.media.length > 0;
        return (
          '<li class="babeina-rev' +
          (hasMedia ? ' babeina-rev--has-media' : '') +
          '">' +
          '<div class="babeina-rev__identity">' +
          '<div class="babeina-rev__author-row">' +
          '<span class="babeina-rev__author">' +
          escapeHtml(rev.author) +
          '</span>' +
          verified +
          /* Mobile: score + stars on author row (hidden on desktop) */
          (scoreNum || starsHtml
            ? '<span class="babeina-rev__rating">' + scoreNum + starsHtml + '</span>'
            : '') +
          '</div>' +
          date +
          '</div>' +
          renderMedia(rev) +
          '<div class="babeina-rev__body">' +
          /* Desktop: stars lead the body (hidden on mobile — shown in .babeina-rev__rating) */
          (starsHtml
            ? starsHtml.replace(
                'class="babeina-rev__stars"',
                'class="babeina-rev__stars babeina-rev__stars--desktop"'
              )
            : '') +
          title +
          (rev.bodyHtml || '') +
          '</div>' +
          /* Desktop: numeric score flush right (hidden on mobile) */
          (scoreNum ? '<span class="babeina-rev__meta">' + scoreNum + '</span>' : '') +
          '</li>'
        );
      })
      .join('');
  }

  function fetchWidgetReviews(root, page) {
    var shopDomain = root.getAttribute('data-shop-domain') || '';
    var productId = root.getAttribute('data-product-id') || '';
    var shopUrl =
      root.getAttribute('data-shop-url') ||
      (window.location && window.location.hostname) ||
      '';
    if (!shopDomain || !productId) {
      return Promise.reject(new Error('Missing Judge.me product context'));
    }

    var params = new URLSearchParams();
    params.set('url', shopUrl);
    params.set('shop_domain', shopDomain);
    params.set('platform', 'shopify');
    params.set('product_id', productId);
    params.set('per_page', String(REVIEWS_PER_PAGE));
    params.set('page', String(page || 1));

    return fetch(JM_WIDGET_URL + '?' + params.toString(), {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('Judge.me widget HTTP ' + res.status);
      return res.json();
    });
  }

  function paginationParts(current, total) {
    var parts = [];
    if (total <= 1) return parts;

    function addPage(n) {
      parts.push({ type: 'page', n: n, current: n === current });
    }
    function addEllipsis() {
      if (parts.length && parts[parts.length - 1].type === 'ellipsis') return;
      parts.push({ type: 'ellipsis' });
    }

    if (total <= 5) {
      var i;
      for (i = 1; i <= total; i++) addPage(i);
      return parts;
    }

    addPage(1);

    var windowLeft;
    var windowRight;
    if (current <= 3) {
      windowLeft = 2;
      windowRight = 3;
    } else if (current >= total - 2) {
      windowLeft = total - 2;
      windowRight = total - 1;
    } else {
      windowLeft = current - 1;
      windowRight = current + 1;
    }

    if (windowLeft > 2) addEllipsis();
    for (var p = windowLeft; p <= windowRight; p++) addPage(p);
    if (windowRight < total - 1) addEllipsis();
    addPage(total);

    return parts;
  }

  function renderPagination(root, current, total) {
    var nav = qs(root, '[data-reviews-pagination]');
    var inner = qs(root, '[data-reviews-pagination-inner]');
    var prevBtn = qs(root, '[data-reviews-page-prev]');
    var nextBtn = qs(root, '[data-reviews-page-next]');
    if (!nav || !inner) return;
    if (total <= 1) {
      nav.hidden = true;
      inner.innerHTML = '';
      if (prevBtn) prevBtn.disabled = true;
      if (nextBtn) nextBtn.disabled = true;
      return;
    }
    nav.hidden = false;
    var parts = paginationParts(current, total);
    inner.innerHTML = parts
      .map(function (part) {
        if (part.type === 'ellipsis') {
          return (
            '<span class="babeina-reviews-pagination__ellipsis" aria-hidden="true">&hellip;</span>'
          );
        }
        if (part.current) {
          return (
            '<button type="button" class="babeina-reviews-pagination__page active" aria-current="page" aria-label="Current page, ' +
            part.n +
            '">' +
            part.n +
            '</button>'
          );
        }
        return (
          '<button type="button" class="babeina-reviews-pagination__page" data-reviews-page="' +
          part.n +
          '" aria-label="Page ' +
          part.n +
          '">' +
          part.n +
          '</button>'
        );
      })
      .join('');

    if (prevBtn) {
      prevBtn.disabled = current <= 1;
      prevBtn.setAttribute('data-reviews-page', String(Math.max(1, current - 1)));
    }
    if (nextBtn) {
      nextBtn.disabled = current >= total;
      nextBtn.setAttribute('data-reviews-page', String(Math.min(total, current + 1)));
    }
  }

  function scrollToReviews(root) {
    var target = qs(root, '.rev-header-row') || root;
    if (window.$s && typeof window.$s.scrollToElement === 'function') {
      window.$s.scrollToElement(target, 16);
      return;
    }
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  function getReviewCache(root) {
    if (!root._babeinaReviewCache) {
      root._babeinaReviewCache = {
        rawMapped: [],
        lastRawPage: 0,
        totalRaw: 0,
        exhausted: false,
        average: '',
      };
    }
    return root._babeinaReviewCache;
  }

  /** Judge.me often returns duplicate rows in one page; dedupe can leave fewer than per_page. */
  function ensureUniqueReviewsLoaded(root, minUniqueCount) {
    var cache = getReviewCache(root);
    minUniqueCount = Math.max(1, minUniqueCount || REVIEWS_PER_PAGE);

    function uniqueList() {
      return prepareReviews(cache.rawMapped);
    }

    function resultPayload() {
      var unique = uniqueList();
      var count = cache.totalRaw || unique.length;
      var totalPages = Math.max(1, Math.ceil(count / REVIEWS_PER_PAGE));
      return {
        unique: unique,
        average: cache.average,
        count: count,
        totalPages: totalPages,
      };
    }

    return new Promise(function (resolve, reject) {
      function pump() {
        var unique = uniqueList();
        if (unique.length >= minUniqueCount || cache.exhausted) {
          resolve(resultPayload());
          return;
        }
        var nextPage = cache.lastRawPage + 1;
        fetchWidgetReviews(root, nextPage)
          .then(function (payload) {
            if (!cache.totalRaw) {
              cache.totalRaw = parseInt(payload.number_of_reviews, 10) || 0;
            }
            if (cache.average === '' && payload.average_rating != null) {
              cache.average = String(payload.average_rating);
            }
            var batch = (payload.reviews || []).map(mapApiReviewItem);
            cache.lastRawPage = nextPage;
            if (!batch.length) {
              cache.exhausted = true;
              resolve(resultPayload());
              return;
            }
            cache.rawMapped = cache.rawMapped.concat(batch);
            if (
              cache.totalRaw > 0 &&
              cache.lastRawPage * REVIEWS_PER_PAGE >= cache.totalRaw
            ) {
              cache.exhausted = true;
            }
            pump();
          })
          .catch(reject);
      }
      pump();
    });
  }

  function loadReviewPage(root, listEl, page) {
    if (listEl) listEl.classList.add('is-loading');
    var needUnique = page * REVIEWS_PER_PAGE;
    return ensureUniqueReviewsLoaded(root, needUnique)
      .then(function (result) {
        var start = (page - 1) * REVIEWS_PER_PAGE;
        var data = {
          reviews: result.unique.slice(start, start + REVIEWS_PER_PAGE),
          average: result.average,
          count: result.count,
          totalPages: result.totalPages,
        };
        root.dataset.reviewsPage = String(page);
        root.dataset.reviewsTotalPages = String(result.totalPages);
        applyReviewsData(root, listEl, data);
        renderPagination(root, page, result.totalPages);
        if (page > 1) scrollToReviews(root);
      })
      .finally(function () {
        if (listEl) listEl.classList.remove('is-loading');
      });
  }

  function fillHeader(root, data) {
    var scoreEl = qs(root, '[data-rev-score]');
    var starsEl = qs(root, '[data-rev-stars]');
    var avg =
      data.average || (scoreEl && scoreEl.getAttribute('data-initial')) || '';
    if (scoreEl && avg && !scoreEl.textContent) {
      scoreEl.textContent = parseFloat(avg).toFixed(1);
    }
    if (starsEl && avg) {
      starsEl.innerHTML = starsRow(avg, 22);
    }
  }

  /* ── Write-review drawer (mirrors #product-drawer open/close) ── */

  function getDrawer(root) {
    return qs(root, '[data-review-drawer]') || qs(document, '[data-review-drawer]');
  }

  function setRating(drawer, value) {
    var input = qs(drawer, '[data-review-rating]');
    var buttons = qsa(drawer, '[data-review-star]');
    var n = parseInt(value, 10) || 0;
    if (input) input.value = n > 0 ? String(n) : '';
    buttons.forEach(function (btn) {
      var star = parseInt(btn.getAttribute('data-review-star'), 10);
      var on = star <= n;
      btn.setAttribute('aria-checked', on && star === n ? 'true' : 'false');
      btn.classList.toggle('is-active', on);
      var path = qs(btn, 'path');
      if (path) path.setAttribute('fill', on ? '#1d1d1f' : '#d0d0d0');
    });
  }

  function showFormState(drawer, mode) {
    var form = qs(drawer, '[data-review-form]');
    var success = qs(drawer, '[data-review-success]');
    var err = qs(drawer, '[data-review-error]');
    if (form) form.hidden = mode === 'success';
    if (success) success.hidden = mode !== 'success';
    if (err && mode !== 'error') {
      err.hidden = true;
      err.textContent = '';
    }
  }

  function openDrawer(drawer) {
    if (!drawer || drawer.getAttribute('aria-hidden') === 'false') return;
    showFormState(drawer, 'form');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    if (window.$s) {
      $s.hideScroll();
      var panel = qs(drawer, '.babeina-review-drawer__panel');
      if (panel) $s.trapFocus(panel);
    }
    var first = qs(drawer, '[data-review-star="1"]') || qs(drawer, 'input, textarea, button');
    if (first && typeof first.focus === 'function') {
      setTimeout(function () {
        first.focus();
      }, 50);
    }
  }

  function closeDrawer(drawer) {
    if (!drawer || drawer.getAttribute('aria-hidden') === 'true') return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    if (window.$s) {
      $s.showScroll();
      $s.removeTrapFocus();
    }
  }

  function resetForm(drawer) {
    var form = qs(drawer, '[data-review-form]');
    if (form) form.reset();
    setRating(drawer, 0);
    showFormState(drawer, 'form');
    var submit = qs(drawer, '[data-review-submit]');
    if (submit) {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  }

  function bindDrawer(root) {
    var drawer = getDrawer(root);
    if (!drawer || drawer.dataset.bound === '1') return drawer;
    drawer.dataset.bound = '1';
    drawer.setAttribute('inert', '');

    qsa(drawer, '[data-review-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        closeDrawer(drawer);
      });
    });

    qsa(drawer, '[data-review-star]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        setRating(drawer, btn.getAttribute('data-review-star'));
      });
      btn.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        setRating(drawer, btn.getAttribute('data-review-star'));
      });
    });

    var form = qs(drawer, '[data-review-form]');
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        submitReview(root, drawer, form);
      });
    }

    var writeAnother = qs(drawer, '[data-review-write-another]');
    if (writeAnother) {
      writeAnother.addEventListener('click', function (e) {
        e.preventDefault();
        resetForm(drawer);
      });
    }

    if (document.documentElement.dataset.babeinaReviewEscBound !== '1') {
      document.documentElement.dataset.babeinaReviewEscBound = '1';
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var open = qs(document, '[data-review-drawer][aria-hidden="false"]');
        if (open) closeDrawer(open);
      });
    }

    return drawer;
  }

  function submitReview(root, drawer, form) {
    var err = qs(drawer, '[data-review-error]');
    var submit = qs(drawer, '[data-review-submit]');
    var ratingInput = qs(drawer, '[data-review-rating]');
    var rating = parseInt(ratingInput && ratingInput.value, 10) || 0;
    var shopDomain = root.getAttribute('data-shop-domain') || '';
    var productId = parseInt(root.getAttribute('data-product-id') || '0', 10) || 0;

    function fail(msg) {
      if (err) {
        err.hidden = false;
        err.textContent = msg || 'Something went wrong. Please try again.';
      }
      if (submit) {
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
      }
    }

    if (!shopDomain || !productId) {
      fail('This product is missing review settings. Please refresh and try again.');
      return;
    }
    if (rating < 1 || rating > 5) {
      fail('Please select a star rating.');
      return;
    }

    var fd = new FormData(form);
    var name = String(fd.get('name') || '').trim();
    var email = String(fd.get('email') || '').trim();
    var title = String(fd.get('title') || '').trim();
    var body = String(fd.get('body') || '').trim();

    if (!name || !email || !body) {
      fail('Please fill in your name, email, and review.');
      return;
    }

    if (err) {
      err.hidden = true;
      err.textContent = '';
    }
    if (submit) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
    }

    // form-urlencoded = CORS-simple request (no OPTIONS preflight). JM returns ACAO *.
    var params = new URLSearchParams();
    params.set('shop_domain', shopDomain);
    params.set('platform', 'shopify');
    params.set('id', String(productId));
    params.set('name', name);
    params.set('email', email);
    params.set('rating', String(rating));
    params.set('body', body);
    if (title) params.set('title', title);

    fetch(JM_CREATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: 'application/json',
      },
      body: params.toString(),
    })
      .then(function (res) {
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            return { ok: res.ok || res.status === 201, status: res.status, data: data };
          });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg =
            (result.data && (result.data.error || result.data.message)) ||
            'Unable to submit your review right now.';
          fail(msg);
          return;
        }
        showFormState(drawer, 'success');
        if (submit) {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
        var successFocus = qs(drawer, '[data-review-success] [data-review-drawer-close]');
        if (successFocus && typeof successFocus.focus === 'function') successFocus.focus();
      })
      .catch(function () {
        fail('Network error. Check your connection and try again.');
      });
  }

  function applyReviewsData(root, listEl, data) {
    var scoreEl = qs(root, '[data-rev-score]');
    if (scoreEl && scoreEl.getAttribute('data-initial')) {
      data.average = data.average || scoreEl.getAttribute('data-initial');
    }
    fillHeader(root, data);
    renderList(listEl, data);
  }

  function enrichFromWidgetApi(root, listEl) {
    if (!root || root.dataset.babeinaReviewsApi === '1') return;
    root.dataset.babeinaReviewsApi = '1';
    loadReviewPage(root, listEl, 1).catch(function () {
      /* keep metafield fallback */
    });
  }

  function bindPagination(root, listEl) {
    root.addEventListener('click', function (e) {
      var btn = e.target.closest(
        '[data-reviews-page], [data-reviews-page-prev], [data-reviews-page-next]'
      );
      if (!btn || !root.contains(btn) || btn.disabled) return;
      e.preventDefault();
      var page = parseInt(btn.getAttribute('data-reviews-page'), 10);
      var current = parseInt(root.dataset.reviewsPage || '1', 10);
      if (!page || page === current) return;
      loadReviewPage(root, listEl, page).catch(function () {});
    });
  }

  function whenNearViewport(el, cb) {
    if (!el || typeof cb !== 'function') return;
    if (!('IntersectionObserver' in window)) {
      cb();
      return;
    }
    var done = false;
    var run = function () {
      if (done) return;
      done = true;
      cb();
    };
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            io.disconnect();
            run();
            break;
          }
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 }
    );
    io.observe(el);
  }

  function initRoot(root) {
    if (!root || root.dataset.babeinaReviewsInit === '1') return;
    root.dataset.babeinaReviewsInit = '1';

    var tpl = qs(root, '[data-jm-reviews-template]');
    var listEl = qs(root, '[data-babeina-reviews-list]');
    var fallback = parseReviewsFromTemplate(tpl);

    // Paint metafield reviews immediately; upgrade from widget API for photos/videos
    // only once the reviews block is near the viewport (keeps JM off the LCP path).
    applyReviewsData(root, listEl, fallback);
    bindPagination(root, listEl);
    whenNearViewport(root, function () {
      enrichFromWidgetApi(root, listEl);
    });

    var drawer = bindDrawer(root);

    var writeBtn = qs(root, '[data-rev-write]');
    if (writeBtn && drawer) {
      writeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        // Ensure media-capable list is warm if shopper jumps straight to write.
        enrichFromWidgetApi(root, listEl);
        openDrawer(drawer);
      });
    }
  }

  function boot() {
    qsa(document, '[data-babeina-reviews]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.BabeinaReviewDrawer = {
    open: function () {
      var root = qs(document, '[data-babeina-reviews]');
      var drawer = root && getDrawer(root);
      if (drawer) openDrawer(drawer);
    },
    close: function () {
      var drawer = qs(document, '[data-review-drawer][aria-hidden="false"]');
      if (drawer) closeDrawer(drawer);
    },
  };
})();
